import React, { useState, useCallback } from 'react';
import ReactPlayer from 'react-player';
import { Loader } from 'semantic-ui-react';
import styles from './VideoPlayer.module.scss';
import { formatDuration } from '../../../models/feed';

interface VideoPlayerProps {
    videoUrl: string;
    thumbnailUrl?: string | null;
    duration?: number | null;
    height: number;
    autoPlay?: boolean;
    onEnded?: () => void;
    onError?: (error: Error) => void;
}

/**
 * VideoPlayer Component
 * Displays a video with thumbnail preview and playback controls
 */
export const VideoPlayer: React.FC<VideoPlayerProps> = ({
    videoUrl,
    thumbnailUrl,
    duration,
    height,
    autoPlay = false,
    onEnded,
    onError,
}) => {
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [showThumbnail, setShowThumbnail] = useState(!autoPlay);

    const handlePlay = useCallback(() => {
        setShowThumbnail(false);
        setIsPlaying(true);
        setIsLoading(true);
    }, []);

    const handleReady = useCallback(() => {
        setIsLoading(false);
    }, []);

    const handleError = useCallback(
        (error: Error) => {
            console.error('[VideoPlayer] Error:', error);
            setHasError(true);
            setIsLoading(false);
            onError?.(error);
        },
        [onError]
    );

    const handleEnded = useCallback(() => {
        setShowThumbnail(true);
        setIsPlaying(false);
        onEnded?.();
    }, [onEnded]);

    const handleRetry = useCallback(() => {
        setHasError(false);
        setShowThumbnail(true);
    }, []);

    // Show thumbnail with play button
    if (showThumbnail && !autoPlay) {
        return (
            <button
                className={`${styles.container} ${styles.containerButton}`}
                style={{ height }}
                onClick={handlePlay}
                aria-label="Play video"
            >
                {thumbnailUrl ? (
                    <div
                        className={styles.thumbnailContainer}
                        style={{ backgroundImage: `url(${thumbnailUrl})` }}
                    >
                        <img
                            src={thumbnailUrl}
                            alt="Video thumbnail"
                            className={styles.thumbnail}
                        />
                    </div>
                ) : (
                    <div className={styles.placeholderThumbnail} />
                )}
                <div className={styles.playButtonOverlay}>
                    <div className={styles.playButton}>
                        <i className="play icon" />
                    </div>
                </div>
                {duration && duration > 0 && (
                    <div className={styles.durationBadge}>
                        {formatDuration(duration)}
                    </div>
                )}
            </button>
        );
    }

    // Show error state
    if (hasError) {
        return (
            <button
                className={`${styles.container} ${styles.errorContainer} ${styles.containerButton}`}
                style={{ height }}
                onClick={handleRetry}
                aria-label="Retry loading video"
            >
                <i className="exclamation triangle icon large" />
                <p className={styles.errorText}>Failed to load video</p>
                <p className={styles.retryText}>Click to retry</p>
            </button>
        );
    }

    // Show video player
    return (
        <div className={styles.container} style={{ height }}>
            <ReactPlayer
                url={videoUrl}
                playing={isPlaying}
                controls
                width="100%"
                height="100%"
                onReady={handleReady}
                onEnded={handleEnded}
                onError={handleError}
                config={{
                    file: {
                        attributes: {
                            controlsList: 'nodownload',
                        },
                    },
                }}
            />
            {isLoading && (
                <div className={styles.loadingOverlay}>
                    <Loader active inverted />
                </div>
            )}
        </div>
    );
};
